import {ChangeEvent, useMemo, useState} from "react";
import {
  SelectField,
  TextField,
  FieldLabel,
  Button,
  CheckboxItem,
  FlexRow,
  Paragraph,
} from "@reside/ui";
import {Form, Formik} from "formik";
import {useTemplateNamesQuery} from "./hooks/useTemplateNamesQuery";
import {useTemplateFieldsQuery} from "./hooks/useTemplateFieldsQuery";
import {usePatchTemplateField} from "./hooks/usePatchTemplateField";
import {BackdropSpinner} from "../../atoms/spinner";
import {useEventTriggersQuery} from "./hooks/useEventTriggersQuery";
import {useTemplateWorkflowsQuery} from "./hooks/useTemplateWorkflowsQuery";
import {WorkflowType} from "../../utils/enums";
import {getWorkflowLabel} from "../../atoms/start-workflow/workflowMapping";
import {HubAndSpokePage} from "./atoms/HubAndSpokePage";
import {
  FieldGrid,
  FormCardWrapper,
  FormSectionHint,
  TemplateFieldCard,
  TemplateFieldRow,
} from "./styles";

export const TemplateFields = () => {
  const [templateName, setTemplateName] = useState<string>();
  const [workflowType, setWorkflowType] = useState<string>(
    WorkflowType.ADMISSION,
  );
  const [offset, setOffset] = useState(30);

  const {isLoading: isLoadingTemplateNames, data: templateNames = []} =
    useTemplateNamesQuery();
  const {data: workflows = [], isLoading: isLoadingWorkflows} =
    useTemplateWorkflowsQuery(templateName);
  const {data: templateFields, isLoading: isLoadingTemplateFields} =
    useTemplateFieldsQuery(templateName, workflowType);
  const {
    mutate: patchTemplateField,
    isLoading: isLoadingPatchTemplateField,
    variables: patchedTemplateField,
  } = usePatchTemplateField();
  const {isLoading: isLoadingEventTriggers, data: eventTriggers = []} =
    useEventTriggersQuery({templateName});

  const templateFieldInUse = (templateFieldId: string) => {
    return Array.isArray(eventTriggers)
      ? eventTriggers
          .map(eventTrigger => eventTrigger.template_field.id)
          .includes(templateFieldId)
      : eventTriggers.id === templateFieldId;
  };

  const initialValues = useMemo(() => {
    return templateFields
      ? templateFields.reduce(
          (accum, templateField) => ({
            ...accum,
            [templateField.id]: templateField.friendly_name,
            [`available-${templateField.id}`]:
              templateField.is_available_in_rule,
          }),
          {templateName, workflowType},
        )
      : {templateName, workflowType};
  }, [templateFields, templateName, workflowType]);

  const currentPage = useMemo(
    () => (templateFields ? templateFields.slice(0, offset) : []),
    [templateFields, offset],
  );

  return (
    <HubAndSpokePage title="Template Fields">
      <FormCardWrapper>
        <Formik<Record<string, string | boolean>>
          enableReinitialize
          initialValues={initialValues}
          onSubmit={() => undefined}
        >
          {({values, setValues}) => (
            <Form>
              <BackdropSpinner
                active={
                  isLoadingTemplateNames ||
                  isLoadingWorkflows ||
                  isLoadingTemplateFields
                }
              >
                <FieldGrid>
                  <SelectField
                    options={templateNames.map(template => ({
                      label: template,
                      value: template,
                    }))}
                    label="Template"
                    name="templateName"
                    onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                      const newTemplateName = event.target.value;
                      setTemplateName(newTemplateName);
                      setWorkflowType(WorkflowType.ADMISSION);
                      setOffset(30);
                    }}
                  />
                  {workflows.length > 1 && (
                    <SelectField
                      options={workflows.map(workflow => ({
                        label: getWorkflowLabel(workflow),
                        value: workflow,
                      }))}
                      label="Workflow"
                      name="workflowType"
                      onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                        setWorkflowType(event.target.value);
                        setOffset(30);
                      }}
                    />
                  )}
                </FieldGrid>
                {!templateName && (
                  <FormSectionHint>
                    Select a template to edit its fields' friendly names and
                    whether they can be used in event triggers.
                  </FormSectionHint>
                )}
                {currentPage.map(templateField => {
                  const isSaving =
                    isLoadingPatchTemplateField &&
                    patchedTemplateField?.templateFieldId === templateField.id;

                  return (
                    !templateField.is_deleted && (
                      <TemplateFieldCard key={templateField.id}>
                        <Paragraph fontSize={18}>
                          {templateField.question_translation_text}
                        </Paragraph>
                        <FieldLabel>
                          {templateField.answer_translation_text}
                        </FieldLabel>
                        <FlexRow>
                          <CheckboxItem
                            label="Is available in rule?"
                            checked={!!values[`available-${templateField.id}`]}
                            onChange={event =>
                              setValues(values => ({
                                ...values,
                                [`available-${templateField.id}`]:
                                  event.target.checked,
                              }))
                            }
                            disabled={
                              isLoadingEventTriggers ||
                              templateFieldInUse(templateField.id)
                            }
                          />
                        </FlexRow>
                        <TemplateFieldRow>
                          <TextField
                            name={templateField.id}
                            label="Friendly name"
                            disabled={isSaving}
                          />
                          <div>
                            <Button
                              color="primary"
                              onClick={() =>
                                patchTemplateField({
                                  templateFieldId: templateField.id,
                                  friendlyName: values[
                                    templateField.id
                                  ] as string,
                                  isAvailableInRule: values[
                                    `available-${templateField.id}`
                                  ] as boolean,
                                })
                              }
                              disabled={isSaving}
                            >
                              {isSaving ? "Saving..." : "Save"}
                            </Button>
                          </div>
                        </TemplateFieldRow>
                      </TemplateFieldCard>
                    )
                  );
                })}
                {templateFields && offset < templateFields.length ? (
                  <Button onClick={() => setOffset(offset + 30)}>
                    Load more
                  </Button>
                ) : (
                  ""
                )}
              </BackdropSpinner>
            </Form>
          )}
        </Formik>
      </FormCardWrapper>
    </HubAndSpokePage>
  );
};
